import type { InvitationDto, MemberDto, RoleDto } from '@cartly/shared';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Mail, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatDate, formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

interface TeamData {
  members: MemberDto[];
  invitations: InvitationDto[];
  roles: RoleDto[];
}

function InviteModal({
  open,
  roles,
  onClose,
  onInvited,
}: {
  open: boolean;
  roles: RoleDto[];
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const defaultRole = roles.find((r) => r.key === 'agent') ?? roles[0];
  const [roleId, setRoleId] = useState(defaultRole?.id ?? '');
  const toast = useToast();
  const submit = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const invited = await submit.run(() =>
      api.post<InvitationDto>('/invitations', { email, roleId }),
    );
    if (!invited) return;
    toast.success(`Invitation sent to ${invited.email}`);
    setEmail('');
    onInvited();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Invite a teammate"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="invite-form" loading={submit.pending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="invite-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={submit.fieldErrors['email']}
        />
        <SelectField
          label="Role"
          value={roleId}
          options={roles.map((r) => ({ value: r.id, label: r.name }))}
          onChange={(e) => setRoleId(e.target.value)}
          hint={roles.find((r) => r.id === roleId)?.description}
          error={submit.fieldErrors['roleId']}
        />
      </form>
    </Modal>
  );
}

export function MembersSettings() {
  const session = useActiveSession();
  const { can, refresh } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const manage = can('members:manage');
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data, error, loading, reload } = useLoader<TeamData>(async () => {
    const [members, invitations, roles] = await Promise.all([
      api.get<{ items: MemberDto[] }>('/members'),
      api.get<{ items: InvitationDto[] }>('/invitations'),
      api.get<{ items: RoleDto[] }>('/roles'),
    ]);
    return { members: members.items, invitations: invitations.items, roles: roles.items };
  }, session.activeTenant?.id);

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;

  const act = async (fn: () => Promise<unknown>, success: string) => {
    try {
      await fn();
      toast.success(success);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const leave = async (member: MemberDto) => {
    const ok = await confirm({
      title: `Leave ${session.activeTenant?.name}?`,
      message: 'You will lose access immediately.',
      confirmLabel: 'Leave',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/members/${member.id}`);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack-lg">
      <Card
        title="Team members"
        subtitle={`${data.members.length} ${data.members.length === 1 ? 'member' : 'members'}`}
        actions={
          manage && (
            <Button
              variant="primary"
              icon={<UserPlus size={16} />}
              onClick={() => setInviteOpen(true)}
            >
              Invite
            </Button>
          )
        }
        flush
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => {
                const isSelf = m.userId === session.user.id;
                return (
                  <tr key={m.id}>
                    <td>
                      <div className="row">
                        <Avatar name={m.name} size="sm" />
                        <div>
                          <div className="table__primary">
                            {m.name} {isSelf && <Badge>You</Badge>}
                          </div>
                          <div className="table__secondary">{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {manage && !isSelf ? (
                        <select
                          className="select select--inline"
                          aria-label={`Role for ${m.name}`}
                          value={m.role.id}
                          onChange={(e) =>
                            void act(
                              () => api.patch(`/members/${m.id}`, { roleId: e.target.value }),
                              'Role updated',
                            )
                          }
                        >
                          {data.roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        m.role.name
                      )}
                    </td>
                    <td>
                      <Badge tone={m.status === 'active' ? 'success' : 'warning'}>{m.status}</Badge>
                    </td>
                    <td className="text-muted">{formatDate(m.joinedAt)}</td>
                    <td className="table__actions">
                      {isSelf ? (
                        <Button size="sm" variant="ghost" onClick={() => void leave(m)}>
                          Leave
                        </Button>
                      ) : (
                        manage && (
                          <div className="row row--end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void act(
                                  () =>
                                    api.patch(`/members/${m.id}`, {
                                      status: m.status === 'active' ? 'suspended' : 'active',
                                    }),
                                  m.status === 'active' ? 'Member suspended' : 'Member reactivated',
                                )
                              }
                            >
                              {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                const ok = await confirm({
                                  title: `Remove ${m.name}?`,
                                  message: 'They lose access to this organization immediately.',
                                  confirmLabel: 'Remove',
                                  tone: 'danger',
                                });
                                if (ok)
                                  void act(() => api.delete(`/members/${m.id}`), 'Member removed');
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Pending invitations" flush>
        {data.invitations.length === 0 ? (
          <EmptyState icon={<Mail size={22} />} title="No pending invitations">
            Invitations you send appear here until they're accepted. They expire after 7 days.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.invitations.map((i) => (
                  <tr key={i.id}>
                    <td className="table__primary">{i.email}</td>
                    <td>{i.role.name}</td>
                    <td className="text-muted">{formatRelative(i.expiresAt)}</td>
                    <td className="table__actions">
                      {manage && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void act(() => api.delete(`/invitations/${i.id}`), 'Invitation revoked')
                          }
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {inviteOpen && (
        <InviteModal
          open={inviteOpen}
          roles={data.roles}
          onClose={() => setInviteOpen(false)}
          onInvited={() => void reload()}
        />
      )}
    </div>
  );
}
